import type { ContractOffer as ContractOrderOffer } from "@frontier/content/contract-orders";
import { useState } from "react";
import { REGIONS } from "@frontier/content/contracts";
import type { ContractSelection } from "@frontier/content/contracts";
import { CUSTOMERS } from "@frontier/content/progression";
import {
  contractAccessTerms,
  contractRewardTerms,
} from "@frontier/content/reward-rules";
import type {
  ContractDifficulty,
  Reputation,
} from "@frontier/content/progression";
import { credits } from "./ui-labels";
import { currentContractDescription } from "./contract-description";
import { passiveInfo } from "./flight-labels";
import { equipmentDefinition, sameEquipmentKey, type EquipmentKey } from "@frontier/content/arsenal";
import { NPCS } from "@frontier/content/npcs";
import { AchievementArt } from "./AchievementArt";
import "./progression.css";
import { EscortRoutePreview } from "./EscortRoutePreview";
import "./station-pages.css";
import "./contract-roster.css";

interface Props {
  knownBlueprints?: readonly EquipmentKey[];
  offers: readonly ContractOrderOffer[];
  selected: readonly ContractSelection[];
  reputation: Reputation;
  busy: boolean;
  onChoose: (contracts: ContractSelection[]) => Promise<void>;
}

export function toggleContractSelection(
  selected: readonly ContractSelection[],
  choice: ContractSelection,
): ContractSelection[] | null {
  const existing = selected.find(
    (item) => item.contractId === choice.contractId,
  );
  if (
    existing?.orderId === choice.orderId &&
    existing.difficulty === choice.difficulty
  )
    return selected.filter((item) => item.contractId !== choice.contractId);
  if (existing)
    return selected.map((item) =>
      item.contractId === choice.contractId ? choice : item,
    );
  return selected.length < 3 ? [...selected, choice] : null;
}

export function ContractOffer({
  offer,
  difficulty,
  selected,
  reputation,
  busy,
  onChoose,
  knownBlueprints = [],
}: Props & {
  offer: ContractOrderOffer;
  index: number;
  difficulty: ContractDifficulty;
}) {
  const id = offer.contractId;
  const contract = offer[difficulty];
  const reward = contractRewardTerms(contract, difficulty);
  const access = contractAccessTerms(contract, difficulty);
  const advancedAccess = contractAccessTerms(offer.advanced, "advanced");
  const customer = reward.reputation?.customerId ?? access?.customerId ?? null;
  const balance = customer ? reputation[customer] : 0;
  const accessBalance = access ? reputation[access.customerId] : 0;
  const advancedBalance = advancedAccess
    ? reputation[advancedAccess.customerId]
    : 0;
  const locked = access !== null && accessBalance < access.minimumReputation;
  const accepted = selected.find((item) => item.contractId === id);
  const chosen =
    accepted?.orderId === offer.orderId && accepted.difficulty === difficulty;
  const anotherRoute =
    accepted !== undefined && accepted.orderId !== offer.orderId;
  const selection = toggleContractSelection(selected, {
    orderId: offer.orderId,
    contractId: id,
    difficulty,
  });
  const region = REGIONS.find((item) => item.id === contract.sectorId);
  const purchaseUnlocks = contract.contentVersion === "contract-orders-v5"
    ? reward.blueprints.filter(blueprint => !knownBlueprints.some(key => sameEquipmentKey(key, blueprint.key))) : [];
  const action = chosen ? "Снять" : accepted ? "Заменить" : "Принять";
  const objective = contract.requiredArchetypes.length
    ? `Уничтожь: ${contract.requiredArchetypes.map(id => NPCS[id].name).join(", ")} · зачётов: ${contract.goal}${contract.requireElite ? " · командир" : ""}`
    : contract.objective.name;
  return (
    <article
      className={`contract-route ${chosen ? "chosen" : ""} ${locked ? "locked" : ""}`}
      data-selected={chosen}
    >
      <div className="journal-art" aria-hidden="true">
        {contract.requiredArchetypes[0] && contract.requiredArchetypes[0] !== "transport" ? <AchievementArt target={{ category: "npc", kind: contract.requiredArchetypes[0] }} /> : <img src={contract.escort || contract.requiredArchetypes[0] === "transport" ? "/assets/transport.png" : "/assets/container.png"} alt="" />}
      </div>
      <div className="route-copy">
        <span className="route-sector">
          {region?.name} /{" "}
          {difficulty === "advanced" ? "ПРОДВИНУТЫЙ" : "ОБЫЧНЫЙ"}
        </span>
        <h2>{contract.title}</h2>
        <p className="route-objective">{objective}</p>
        {chosen && <span className="route-accepted">ПРИНЯТО</span>}
        {accepted && !chosen && <span className="route-accepted">
          {anotherRoute ? "Принят другой заказ этого типа" : `Принят ${accepted.difficulty === "advanced" ? "продвинутый" : "обычный"}`}
        </span>}
        {locked && access && <span className="route-lock">Нужно {access.minimumReputation} репутации · сейчас {accessBalance}/{access.minimumReputation}</span>}
        <details className="route-details">
          <summary>Условия и награды</summary>
        {contract.escort && (
          <EscortRoutePreview
            terms={contract.escort}
            name={contract.title}
            compact
          />
        )}
        <p>{currentContractDescription(contract.description)}</p>
        {contract.contentVersion === "contract-orders-v5" ? (
          <small className="route-purchase-unlocks" title={purchaseUnlocks.map(blueprint => `${equipmentDefinition(blueprint.key).name} · ${blueprint.successes} успешных сдач`).join("; ")}>
            {purchaseUnlocks.length > 0
              ? `Откроет покупку: ${purchaseUnlocks.slice(0, 2).map(blueprint => equipmentDefinition(blueprint.key).name + (blueprint.successes > 1 ? ` (${blueprint.successes} сдач)` : "")).join(", ")}${purchaseUnlocks.length > 2 ? ` и ещё ${purchaseUnlocks.length - 2}` : ""}`
              : "Покупки уже открыты · бонус за каждую сдачу"}
          </small>
        ) : null}
        {customer && (
          <span className="route-customer">
            {CUSTOMERS[customer].name} · {balance} РЕП
            {advancedAccess && advancedAccess.minimumReputation > 0 && (
              <progress
                aria-label={`Репутация: ${CUSTOMERS[advancedAccess.customerId].name}`}
                max={advancedAccess.minimumReputation}
                value={Math.min(
                  advancedBalance,
                  advancedAccess.minimumReputation,
                )}
              />
            )}
          </span>
        )}
        {(!accepted || chosen) && (
          difficulty === "standard" &&
          advancedAccess !== null &&
          advancedBalance < advancedAccess.minimumReputation && (
            <span className="route-access">
              Продвинутый: ещё{" "}
              {advancedAccess.minimumReputation - advancedBalance} репутации
            </span>
          )
        )}
        </details>
      </div>
      <div className="route-reward">
        <small className="route-reward-label">Награда</small>
        <b>
          {BigInt(contract.credits) > 0n
            ? `+${credits(contract.credits)} КР`
            : "ГРУЗ → КР"}
        </b>
        <span>
          <span className="journal-amount">+{contract.components} комп.</span>
          {reward.reputation && <> · +{reward.reputation.amount} реп.</>}
        </span>
        {contract.contentVersion !== "contract-orders-v5" && contract.unlock !== "none" && (
          <small>Разовая награда, если ещё не получена: {passiveInfo(contract.unlock).name}</small>
        )}
        <button type="button" className="route-action" aria-label={`${action}: ${contract.title}`}
          disabled={busy || (locked && !chosen) || selection === null}
          onClick={() => { if (!busy && (!locked || chosen) && selection !== null) void onChoose(selection); }}>
          {action.toUpperCase()}
        </button>
      </div>
    </article>
  );
}

export function ContractBoard(props: Props) {
  const [difficulty, setDifficulty] = useState<ContractDifficulty>(
    props.selected[0]?.difficulty ?? "standard",
  );
  const accessThresholds = props.offers.map(
    (offer) =>
      contractAccessTerms(offer.advanced, "advanced")?.minimumReputation ?? 0,
  );
  const minimumReputation =
    accessThresholds.length > 0 ? Math.min(...accessThresholds) : null;
  return (
    <section
      className="contract-board contract-roster"
      aria-label="Выбор контрактов"
    >
      <div className="contract-roster-toolbar">
        <div
          className="contract-difficulties"
          role="group"
          aria-label="Сложность контракта"
        >
          <button
            aria-pressed={difficulty === "standard"}
            onClick={() => setDifficulty("standard")}
          >
            ОБЫЧНЫЕ
          </button>
          <button
            aria-pressed={difficulty === "advanced"}
            onClick={() => setDifficulty("advanced")}
          >
            ПРОДВИНУТЫЕ{" "}
            {minimumReputation !== null && (
              <small>ОТ {minimumReputation} РЕП.</small>
            )}
          </button>
        </div>
        <span className="contract-roster-status" role="status">
          Принято {props.selected.length}/3
          {props.selected.length === 0 && (
            <small> · Свободный вылет</small>
          )}
        </span>
      </div>
      {props.selected.length > 0 && <p className="contract-next-step" role="status">После вылета открой журнал заданий и выбери маршрут. Все принятые задания продолжают выполняться.</p>}
      <div className="contract-routes">
        {props.offers.map((offer, index) => (
          <ContractOffer
            key={offer.orderId}
            {...props}
            offer={offer}
            index={index}
            difficulty={difficulty}
          />
        ))}
        <details className="contract-explainer"><summary>Как работают задания и репутация</summary>
          <p>Можно принять до трёх заданий. В полёте выбери в журнале, какой маршрут показывать на карте. Репутация открывает продвинутые задания соответствующего заказчика; за полное выполнение начисляется репутация, за повторные заказы — новые бонусы.</p>
        </details>
      </div>
    </section>
  );
}
